/**
 * Sector operations — Tab URL parameter parsing and serialization.
 * R39 A1.b (plan docs/plans/sector-operations.md §2 SO-P58).
 */

import { isClinicsSector, isFitnessSector, type OpsSector } from '../../types/ops'
import { SECTOR_HUB_CONFIGS } from './sectorHubConfig'

export type ConstructionSectionTab =
  | 'con-projects'
  | 'con-today'
  | 'con-materials'
  | 'con-quality'
  | 'con-issues'
  | 'con-reports'
  | 'con-drawings'
  | 'con-measure'

export type HospitalSectionTab =
  | 'hos-today'
  | 'hos-requests'
  | 'hos-equipment'
  | 'hos-registers'
  | 'hos-people'
  | 'hos-beds'
  | 'hos-patients'

export type SitesSectionTab =
  | 'sit-today'
  | 'sit-posts'
  | 'sit-rounds'
  | 'sit-incidents'
  | 'sit-client'
  | 'sit-wages'

export type DeliverySectionTab =
  | 'del-today'
  | 'del-engagements'
  | 'del-requests'
  | 'del-uat'
  | 'del-integrations'
  | 'del-reports'
  | 'del-export'

export type ShopsSectionTab =
  | 'shp-today'
  | 'shp-cash'
  | 'shp-stock'
  | 'shp-audits'
  | 'shp-staff'
  | 'shp-kitchen'
  | 'shp-licences'

export type StaysSectionTab =
  | 'sty-today'
  | 'sty-board'
  | 'sty-hk'
  | 'sty-requests'
  | 'sty-linen'
  | 'sty-lost'
  | 'sty-group'
  | 'sty-extras'
  | 'sty-pms'
  | 'sty-registers'

export type JobsSectionTab =
  | 'job-today'
  | 'job-intake'
  | 'job-customers'
  | 'job-amc'
  | 'job-parts'
  | 'job-messages'

export type ClinicsSectionTab =
  | 'cli-today'
  | 'cli-cold-chain'
  | 'cli-waste'
  | 'cli-imaging'
  | 'cli-samples'
  | 'cli-vault'
  | 'cli-hub'
  | 'cli-loops'
  | 'cli-tele'

export type PlantSectionTab =
  | 'plt-today'
  | 'plt-machines'
  | 'plt-registers'
  | 'plt-quality'
  | 'plt-downtime'

export type FitnessSectionTab =
  | 'fit-today'

export type OpsSetupTab =
  | 'core-places'
  | 'core-workforce'
  | 'core-templates'
  | 'core-schedules'
  | 'core-rosters'
  | 'core-requests'
  | 'core-assets'
  | 'core-bookings'
  | 'core-compliance'
  | 'core-whatsapp'
  | 'core-import'
  | 'core-export'
  | 'core-devices'
  | 'core-phone-access'
  | 'core-approval'

export type OpsTab =
  | ConstructionSectionTab
  | HospitalSectionTab
  | SitesSectionTab
  | DeliverySectionTab
  | ShopsSectionTab
  | StaysSectionTab
  | JobsSectionTab
  | ClinicsSectionTab
  | PlantSectionTab
  | FitnessSectionTab
  | OpsSetupTab

export const SECTOR_DEFAULT_TAB: Record<OpsSector, OpsTab> = {
  construction: SECTOR_HUB_CONFIGS.construction.defaultTab,
  hospital: SECTOR_HUB_CONFIGS.hospital.defaultTab,
  sites: SECTOR_HUB_CONFIGS.sites.defaultTab,
  delivery: SECTOR_HUB_CONFIGS.delivery.defaultTab,
  shops: SECTOR_HUB_CONFIGS.shops.defaultTab,
  stays: SECTOR_HUB_CONFIGS.stays.defaultTab,
  jobs: SECTOR_HUB_CONFIGS.jobs.defaultTab,
  clinics: SECTOR_HUB_CONFIGS.clinics.defaultTab,
  plant: SECTOR_HUB_CONFIGS.plant.defaultTab,
  fitness: SECTOR_HUB_CONFIGS.fitness.defaultTab,
}

const COMMON_SETUP_TABS = new Set<OpsTab>([
  'core-places',
  'core-workforce',
  'core-templates',
  'core-schedules',
  'core-rosters',
  'core-requests',
  'core-assets',
  'core-bookings',
  'core-compliance',
  'core-whatsapp',
  'core-import',
  'core-export',
  'core-devices',
  'core-phone-access',
  'core-approval',
])

export function parseOpsTabParam(
  search: string,
  sector?: OpsSector,
): OpsTab | null {
  if (!search) return null
  const searchStr = search.startsWith('?') ? search.slice(1) : search
  const params = new URLSearchParams(searchStr)
  const tab = params.get('tab') ?? searchStr
  if (!tab) return null

  const normalized = tab.toLowerCase().trim()

  // 1. Check if it's a known setup tab alias
  switch (normalized) {
    case 'core-places':
    case 'places':
      return 'core-places'
    case 'core-workforce':
    case 'workforce':
      return 'core-workforce'
    case 'core-templates':
    case 'templates':
    case 'core-registers':
      return 'core-templates'
    case 'core-schedules':
    case 'schedules':
      return 'core-schedules'
    case 'core-rosters':
    case 'core-roster':
    case 'rosters':
    case 'roster':
      return 'core-rosters'
    case 'core-requests':
      return 'core-requests'
    case 'requests':
      if (
        sector &&
        SECTOR_HUB_CONFIGS[sector]?.sectionTabs.some(
          (t) => t.key === 'hos-requests',
        )
      ) {
        return 'hos-requests'
      }
      if (
        sector &&
        SECTOR_HUB_CONFIGS[sector]?.sectionTabs.some(
          (t) => t.key === 'sty-requests',
        )
      ) {
        return 'sty-requests'
      }
      return 'core-requests'
    case 'core-assets':
    case 'assets':
      return 'core-assets'
    case 'core-bookings':
    case 'bookings':
    case 'desk':
    case 'booking-desk':
      return 'core-bookings'
    case 'core-compliance':
    case 'compliance':
      return 'core-compliance'
    case 'core-whatsapp':
    case 'whatsapp':
      return 'core-whatsapp'
    case 'core-import':
    case 'import':
    case 'csv-import':
    case 'pos-import':
    case 'pos-csv':
      return 'core-import'
    case 'core-export':
      return 'core-export'
    case 'core-devices':
    case 'devices':
    case 'shared-devices':
      return 'core-devices'
    case 'core-phone-access':
    case 'phone-access':
    case 'phone':
      return 'core-phone-access'
  }

  // 2. Check bare alias or sector-specific tab
  if (normalized === 'today') {
    if (sector && SECTOR_HUB_CONFIGS[sector]) {
      return SECTOR_HUB_CONFIGS[sector].defaultTab
    }
    return 'con-today'
  }

  // Sector-specific tabs
  let parsed: OpsTab | null = null

  // Sites
  if (normalized === 'sit-today') parsed = 'sit-today'
  else if (normalized === 'sit-posts' || normalized === 'posts') parsed = 'sit-posts'
  else if (normalized === 'sit-rounds' || normalized === 'rounds') parsed = 'sit-rounds'
  else if (normalized === 'sit-incidents' || normalized === 'incidents') parsed = 'sit-incidents'
  else if (normalized === 'sit-client' || normalized === 'client') parsed = 'sit-client'
  else if (normalized === 'sit-wages' || normalized === 'wages') parsed = 'sit-wages'

  // Construction
  else if (normalized === 'con-projects' || normalized === 'projects') parsed = 'con-projects'
  else if (normalized === 'con-today') parsed = 'con-today'
  else if (normalized === 'con-materials' || normalized === 'materials') parsed = 'con-materials'
  else if (normalized === 'con-quality' || normalized === 'quality') parsed = 'con-quality'
  else if (normalized === 'con-issues' || normalized === 'issues') parsed = 'con-issues'
  else if (normalized === 'con-reports' || normalized === 'reports') parsed = 'con-reports'
  else if (normalized === 'con-drawings' || normalized === 'drawings') parsed = 'con-drawings'
  else if (
    normalized === 'con-measure' ||
    normalized === 'measure' ||
    normalized === 'measurement'
  )
    parsed = 'con-measure'

  // Hospital
  else if (normalized === 'hos-today') parsed = 'hos-today'
  else if (normalized === 'hos-requests') parsed = 'hos-requests'
  else if (normalized === 'hos-equipment' || normalized === 'equipment') parsed = 'hos-equipment'
  else if (normalized === 'hos-registers' || (sector !== 'stays' && normalized === 'registers')) parsed = 'hos-registers'
  else if (normalized === 'hos-people' || normalized === 'people') parsed = 'hos-people'
  else if (
    normalized === 'hos-beds' ||
    normalized === 'beds' ||
    normalized === 'bed-board'
  )
    parsed = 'hos-beds'
  else if (normalized === 'hos-patients' || normalized === 'patients') parsed = 'hos-patients'

  // Delivery
  else if (normalized === 'del-today') parsed = 'del-today'
  else if (normalized === 'del-engagements' || normalized === 'engagements') parsed = 'del-engagements'
  else if (
    normalized === 'del-requests' ||
    normalized === 'del-crs' ||
    normalized === 'crs' ||
    normalized === 'change-requests'
  )
    parsed = 'del-requests'
  else if (normalized === 'del-uat' || normalized === 'uat') parsed = 'del-uat'
  else if (normalized === 'del-integrations' || normalized === 'integrations') parsed = 'del-integrations'
  else if (normalized === 'del-reports') parsed = 'del-reports'
  else if (normalized === 'del-export' || (sector === 'delivery' && normalized === 'export')) parsed = 'del-export'

  // Shops
  else if (normalized === 'shp-today') parsed = 'shp-today'
  else if (normalized === 'shp-cash' || normalized === 'cash' || normalized === 'handover') parsed = 'shp-cash'
  else if (normalized === 'shp-stock' || (sector === 'shops' && normalized === 'stock')) parsed = 'shp-stock'
  else if (normalized === 'shp-audits' || normalized === 'audits' || normalized === 'display-audits') parsed = 'shp-audits'
  else if (normalized === 'shp-staff' || (sector === 'shops' && normalized === 'staff')) parsed = 'shp-staff'
  else if (normalized === 'shp-kitchen' || normalized === 'kitchen') parsed = 'shp-kitchen'
  else if (normalized === 'shp-licences' || normalized === 'licences' || normalized === 'licenses') parsed = 'shp-licences'

  // Stays
  else if (normalized === 'sty-today') parsed = 'sty-today'
  else if (normalized === 'sty-board' || normalized === 'board' || normalized === 'room-board') parsed = 'sty-board'
  else if (normalized === 'sty-hk' || normalized === 'hk' || normalized === 'housekeeping') parsed = 'sty-hk'
  else if (normalized === 'sty-requests' || (sector === 'stays' && normalized === 'requests')) parsed = 'sty-requests'
  else if (normalized === 'sty-linen' || normalized === 'linen' || normalized === 'minibar') parsed = 'sty-linen'
  else if (normalized === 'sty-lost' || normalized === 'lost' || normalized === 'lost-and-found') parsed = 'sty-lost'
  else if (normalized === 'sty-group' || normalized === 'group') parsed = 'sty-group'
  else if (normalized === 'sty-extras' || normalized === 'extras' || normalized === 'spa') parsed = 'sty-extras'
  else if (normalized === 'sty-pms' || normalized === 'pms') parsed = 'sty-pms'
  else if (normalized === 'sty-registers' || (sector === 'stays' && (normalized === 'registers' || normalized === 'licences' || normalized === 'licenses'))) parsed = 'sty-registers'

  // Jobs
  else if (normalized === 'job-today') parsed = 'job-today'
  else if (normalized === 'job-intake' || normalized === 'intake') parsed = 'job-intake'
  else if (normalized === 'job-customers' || normalized === 'customers') parsed = 'job-customers'
  else if (normalized === 'job-amc' || normalized === 'amc' || normalized === 'warranty') parsed = 'job-amc'
  else if (normalized === 'job-parts' || (sector === 'jobs' && normalized === 'parts')) parsed = 'job-parts'
  else if (normalized === 'job-messages' || normalized === 'messages') parsed = 'job-messages'

  // Clinics
  else if (normalized === 'cli-today' || (sector && isClinicsSector(sector) && normalized === 'today')) parsed = 'cli-today'
  else if (normalized === 'cli-cold-chain' || (sector && isClinicsSector(sector) && (normalized === 'cold-chain' || normalized === 'cold'))) parsed = 'cli-cold-chain'
  else if (normalized === 'cli-waste' || (sector && isClinicsSector(sector) && (normalized === 'waste' || normalized === 'bmw'))) parsed = 'cli-waste'
  else if (normalized === 'cli-imaging' || (sector && isClinicsSector(sector) && (normalized === 'imaging' || normalized === 'form-f' || normalized === 'pcpndt'))) parsed = 'cli-imaging'
  else if (normalized === 'cli-samples' || (sector && isClinicsSector(sector) && (normalized === 'samples' || normalized === 'custody'))) parsed = 'cli-samples'
  else if (normalized === 'cli-vault' || (sector && isClinicsSector(sector) && (normalized === 'vault' || normalized === 'narcotics'))) parsed = 'cli-vault'
  else if (normalized === 'cli-hub' || (sector && isClinicsSector(sector) && (normalized === 'hub' || normalized === 'spokes'))) parsed = 'cli-hub'
  else if (normalized === 'cli-loops' || (sector && isClinicsSector(sector) && (normalized === 'loops' || normalized === 'specialty'))) parsed = 'cli-loops'
  else if (normalized === 'cli-tele' || (sector && isClinicsSector(sector) && (normalized === 'tele' || normalized === 'teleconsult'))) parsed = 'cli-tele'

  // Plant
  else if (normalized === 'plt-today' || (sector === 'plant' && normalized === 'today')) parsed = 'plt-today'
  else if (normalized === 'plt-machines' || (sector === 'plant' && (normalized === 'machines' || normalized === 'machine'))) parsed = 'plt-machines'
  else if (normalized === 'plt-registers' || (sector === 'plant' && normalized === 'registers')) parsed = 'plt-registers'
  else if (normalized === 'plt-quality' || (sector === 'plant' && (normalized === 'quality' || normalized === 'safety'))) parsed = 'plt-quality'
  else if (normalized === 'plt-downtime' || (sector === 'plant' && normalized === 'downtime')) parsed = 'plt-downtime'

  // Fitness
  else if (normalized === 'fit-today' || (sector && isFitnessSector(sector) && normalized === 'today')) parsed = 'fit-today'

  if (!parsed) {
    if (normalized === 'export' || normalized === 'retention') {
      parsed = 'core-export'
    } else if (normalized === 'devices' || normalized === 'shared-devices') {
      parsed = 'core-devices'
    } else if (normalized === 'phone-access' || normalized === 'phone') {
      parsed = 'core-phone-access'
    } else if (
      normalized === 'core-approval' ||
      normalized === 'approval' ||
      normalized === 'approvers' ||
      normalized === 'client-approvers'
    ) {
      parsed = 'core-approval'
    }
  }

  if (!parsed) return null

  // If sector is specified:
  // "and a tab from another sector falls back to that sector's default."
  if (sector) {
    if (COMMON_SETUP_TABS.has(parsed)) {
      return parsed
    }
    const sectorPrefix = SECTOR_HUB_CONFIGS[sector]?.tabPrefix ?? 'con-'
    if (!parsed.startsWith(sectorPrefix)) {
      return SECTOR_DEFAULT_TAB[sector]
    }
  }

  return parsed
}
